import React, { useState, useEffect, useMemo } from 'react';
import PlayerCard from '../components/PlayerCard';
import ApiService from '../services/api';
import './DirectoryPage.css';

/**
 * DirectoryPage Component
 * Lists all players with search and filter functionality
 */

const DirectoryPage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [sortBy, setSortBy] = useState('ranking');

  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllPlayers();
      setPlayers(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load players. Please try again.');
      console.error('Error loading players:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filters
  const platforms = useMemo(() => {
    const unique = [...new Set(players.map(p => p.Main_Platform).filter(Boolean))];
    return unique.sort();
  }, [players]);

  const classes = useMemo(() => {
    const unique = [...new Set(players.map(p => p.Class).filter(Boolean))];
    return unique.sort();
  }, [players]);

  // Filter and sort players
  const filteredPlayers = useMemo(() => {
    let filtered = players.filter(player => {
      const matchesSearch = 
        player.Player_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.Player_ID?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPlatform = filterPlatform === 'all' || player.Main_Platform === filterPlatform;
      const matchesClass = filterClass === 'all' || player.Class === filterClass;

      return matchesSearch && matchesPlatform && matchesClass;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'ranking':
          const rankA = parseInt(a.Ranking) || 9999;
          const rankB = parseInt(b.Ranking) || 9999;
          return rankA - rankB;
        case 'name':
          return (a.Player_Name || a.Player_ID).localeCompare(b.Player_Name || b.Player_ID);
        case 'wins':
          return (parseInt(b.Wins) || 0) - (parseInt(a.Wins) || 0);
        case 'winpct':
          return parseFloat(b.Win_Percentage || 0) - parseFloat(a.Win_Percentage || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [players, searchTerm, filterPlatform, filterClass, sortBy]);

  if (loading) {
    return (
      <div className="directory-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading players...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="directory-page">
        <div className="error-state">
          <h2>⚠️ {error}</h2>
          <button onClick={loadPlayers} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="directory-page">
      <header className="directory-header">
        <div className="header-content">
          <div className="logo-title">
            <img src="/logo.png" alt="IsoCentral" className="logo" />
            <h1 className="directory-title">IsoCentral Player Directory</h1>
          </div>
          <p className="directory-subtitle">
            {players.length} Players • 1v1 Basketball Rankings
          </p>
        </div>
      </header>

      <div className="directory-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>Platform:</label>
            <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Class:</label>
            <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
              <option value="all">All Classes</option>
              {classes.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="ranking">Ranking</option>
              <option value="name">Name (A-Z)</option>
              <option value="wins">Most Wins</option>
              <option value="winpct">Win Percentage</option>
            </select>
          </div>
        </div>

        <div className="results-count">
          Showing {filteredPlayers.length} of {players.length} players
        </div>
      </div>

      <div className="players-grid">
        {filteredPlayers.map(player => (
          <PlayerCard key={player.Player_ID} player={player} />
        ))}
      </div>

      {filteredPlayers.length === 0 && (
        <div className="no-results">
          <p>No players found matching your criteria.</p>
          <button onClick={() => {
            setSearchTerm('');
            setFilterPlatform('all');
            setFilterClass('all');
          }} className="clear-filters-button">
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default DirectoryPage;
